import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Stack,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  AddReaction as AddReactionIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { Message } from '../../types/message';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import MentionDisplay from '../common/MentionDisplay';

interface MessageItemProps {
  message: Message;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  onReply?: (messageId: string) => void;
}

const COMMON_EMOJIS = ['👍', '❤️', '😊', '🎉', '😂', '🤔', '👏', '🙏'];

/**
 * MessageItem Component
 *
 * Individual message bubble with:
 * - Sender avatar and name
 * - Message content
 * - Timestamp and read status
 * - Reactions
 * - Edit/Delete actions for own messages
 * - Reply functionality
 */
export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage,
  showAvatar = true,
  onReply,
}) => {
  const { authState } = useAuth();
  const user = authState.user;
  const { editMessage, deleteMessage, addReaction, removeReaction } = useMessaging();

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reactionMenuAnchor, setReactionMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleReactionMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setReactionMenuAnchor(event.currentTarget);
  };

  const handleReactionMenuClose = (): void => {
    setReactionMenuAnchor(null);
  };

  const handleEdit = (): void => {
    setIsEditing(true);
    handleMenuClose();
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editContent.trim()) return;

    try {
      await editMessage(message.id, editContent.trim());
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error editing message:', error);
      alert(`Failed to edit message: ${error.message}`);
    }
  };

  const handleCancelEdit = (): void => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleDelete = async (): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteMessage(message.id);
      handleMenuClose();
    } catch (error: any) {
      console.error('Error deleting message:', error);
      alert(`Failed to delete message: ${error.message}`);
    }
  };

  const handleReply = (): void => {
    if (onReply) {
      onReply(message.id);
    }
    handleMenuClose();
  };

  const handleAddReaction = async (emoji: string): Promise<void> => {
    if (!user) return;

    try {
      const userReactions = message.reactions?.[emoji] || [];
      if (userReactions.includes(user.uid)) {
        await removeReaction(message.id, emoji);
      } else {
        await addReaction(message.id, emoji);
      }
      handleReactionMenuClose();
    } catch (error: any) {
      console.error('Error toggling reaction:', error);
    }
  };

  const hasUserReacted = (emoji: string): boolean => {
    if (!user) return false;
    return message.reactions?.[emoji]?.includes(user.uid) || false;
  };

  const getReactionCount = (emoji: string): number => {
    return message.reactions?.[emoji]?.length || 0;
  };

  const uniqueEmojis = message.reactions
    ? Object.keys(message.reactions).filter(emoji => getReactionCount(emoji) > 0)
    : [];

  const canEditDelete = isOwnMessage && !message.deleted;
  const canEdit = canEditDelete && !message.edited;

  // Calculate if message has been read by others
  const hasBeenRead = message.readBy.length > 1; // More than just sender
  const allRead = message.readBy.length > 2; // Read by multiple recipients (for groups)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwnMessage ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        mb: 2,
        gap: 1,
      }}
    >
      {/* Avatar */}
      {showAvatar && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            visibility: isOwnMessage ? 'hidden' : 'visible',
          }}
        >
          {message.senderId[0].toUpperCase()}
        </Avatar>
      )}

      <Box sx={{ maxWidth: '70%' }}>
        {/* Message Bubble */}
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            bgcolor: isOwnMessage ? 'primary.main' : 'background.paper',
            color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            position: 'relative',
          }}
        >
          {/* Message Content */}
          {isEditing ? (
            <Box>
              <TextField
                fullWidth
                multiline
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                size="small"
                autoFocus
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" onClick={handleSaveEdit} variant="contained">
                  Save
                </Button>
                <Button size="small" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              {message.deleted ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                  This message was deleted
                </Typography>
              ) : (
                <>
                  <MentionDisplay
                    content={message.content}
                    mentions={message.mentions || []}
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      display: 'block',
                    }}
                    component="span"
                  />
                  {message.edited && (
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                      (edited)
                    </Typography>
                  )}
                </>
              )}

              {/* Actions Menu */}
              {!message.deleted && (
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Paper>

        {/* Reactions */}
        {uniqueEmojis.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            {uniqueEmojis.map((emoji) => (
              <Chip
                key={emoji}
                label={`${emoji} ${getReactionCount(emoji)}`}
                size="small"
                onClick={() => handleAddReaction(emoji)}
                variant={hasUserReacted(emoji) ? 'filled' : 'outlined'}
                sx={{ height: 24 }}
              />
            ))}
            <Tooltip title="Add reaction">
              <IconButton size="small" onClick={handleReactionMenuOpen}>
                <AddReactionIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* Timestamp and Read Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(message.createdAt, { addSuffix: true })}
          </Typography>
          {isOwnMessage && (
            <>
              {allRead ? (
                <DoneAllIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              ) : hasBeenRead ? (
                <DoneAllIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              ) : (
                <CheckIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {onReply && (
          <MenuItem onClick={handleReply}>
            <ReplyIcon sx={{ mr: 1 }} fontSize="small" />
            Reply
          </MenuItem>
        )}
        <MenuItem onClick={handleReactionMenuOpen}>
          <AddReactionIcon sx={{ mr: 1 }} fontSize="small" />
          Add Reaction
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {canEditDelete && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Reaction Picker Menu */}
      <Menu
        anchorEl={reactionMenuAnchor}
        open={Boolean(reactionMenuAnchor)}
        onClose={handleReactionMenuClose}
      >
        <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
          {COMMON_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleAddReaction(emoji)}
              sx={{
                fontSize: 24,
                border: hasUserReacted(emoji) ? 2 : 0,
                borderColor: 'primary.main',
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Menu>
    </Box>
  );
};
